// templateOverrides — module-level cache + React hook for the
// operator-managed template overrides served by the main backend at
// /api/v1/templates/visibility.
//
// The catalog (TEMPLATES in data.ts) is hardcoded at build-time for
// layout + react-component lookup reasons; this module overlays any
// runtime fields the operator has edited (title, price, badge, colour,
// demo placeholders) on top. Components that previously imported
// TEMPLATES directly can switch to useMergedTemplates() / mergeTemplate()
// to pick up live edits without a redeploy.
//
// Cache strategy: one fetch per session, kept in a module-level promise.
// Re-fetch happens only on page reload. Failure → empty map → defaults
// keep applying (fail-open).

import { useEffect, useMemo, useState } from "react";
import { fetchTemplateOverrides, type TemplateOverrideRow } from "@/lib/api";
import { TEMPLATES, type Template } from "@/lib/data";

let cached: Promise<Map<string, TemplateOverrideRow>> | null = null;

function loadOverrides(): Promise<Map<string, TemplateOverrideRow>> {
  if (!cached) cached = fetchTemplateOverrides();
  return cached;
}

// Apply a single override row onto a hardcoded Template. NULL/undefined
// fields preserve the default; the price_tiyin field is converted to
// the catalog's UZS price. The `badge` enum on Template is narrow
// ("Bestseller" | "Premium" | "New"); any operator-typed badge string
// that doesn't match falls back to the default — we don't want bogus
// values landing in a typed prop.
const ALLOWED_BADGES = new Set(["Bestseller", "Premium", "New"]);

export function mergeTemplate(
  t: Template,
  override: TemplateOverrideRow | undefined,
): Template {
  if (!override) return t;
  const overrideBadge =
    override.badge && ALLOWED_BADGES.has(override.badge)
      ? (override.badge as Template["badge"])
      : undefined;
  return {
    ...t,
    title: override.title ?? t.title,
    category: (override.category as Template["category"]) ?? t.category,
    bg: override.bg ?? t.bg,
    ink: override.ink ?? t.ink,
    accent: override.accent ?? t.accent,
    font: override.font ?? t.font,
    price: override.price_tiyin ? Math.round(override.price_tiyin / 100) : t.price,
    badge: overrideBadge ?? t.badge,
    names: override.demo_names ?? t.names,
    date: override.demo_date ?? t.date,
    venue: override.demo_venue ?? t.venue,
  };
}

// React hook: returns a function that merges any Template through the
// override map. Re-renders once the fetch resolves; meanwhile callers
// see hardcoded defaults — the catalog renders immediately, then
// updates if overrides exist.
export function useTemplateMerger(): (t: Template) => Template {
  const [map, setMap] = useState<Map<string, TemplateOverrideRow>>(new Map());
  useEffect(() => {
    let cancelled = false;
    loadOverrides().then((m) => {
      if (!cancelled) setMap(m);
    });
    return () => {
      cancelled = true;
    };
  }, []);
  return (t: Template) => mergeTemplate(t, map.get(t.id));
}

// React hook: the set of template ids the operator has marked
// `is_public=false`. Backed by the same one-fetch-per-session cache
// as the override map. Catalog/landing pages use this to filter the
// public catalog without losing access to existing invitations.
export function useHiddenIds(): Set<string> {
  const [hidden, setHidden] = useState<Set<string>>(new Set());
  useEffect(() => {
    let cancelled = false;
    loadOverrides().then((m) => {
      if (cancelled) return;
      const h = new Set<string>();
      for (const [id, row] of m) if (!row.is_public) h.add(id);
      setHidden(h);
    });
    return () => {
      cancelled = true;
    };
  }, []);
  return hidden;
}

// React hook: per-category visible-template counts, plus an "all" key
// for the total. Computed against TEMPLATES minus hidden ids, so the
// filter chip badges + landing tiles never claim "98" while the gallery
// only renders 13. Re-derives whenever useHiddenIds resolves.
export function useVisibleCategoryCounts(): Map<string, number> {
  const hidden = useHiddenIds();
  return useMemo(() => {
    const m = new Map<string, number>();
    let all = 0;
    for (const t of TEMPLATES) {
      if (hidden.has(t.id) || t.unlisted) continue;
      all++;
      m.set(t.category, (m.get(t.category) ?? 0) + 1);
    }
    m.set("all", all);
    return m;
  }, [hidden]);
}
